import { ApiTransferDto, ApiTriggersDto } from '@/shared/api/generated/data-contracts';
import {
  ScenarioTransferCondition,
  ScenarioTransferType,
  ScenarioTriggerCondition,
  ScenarioTriggerType,
} from '@/shared/api/types';
import { CollectionName } from '@/shared/lib/sj-orm/types';

import { isAllAtOnceScenario, isOneByOneScenario } from '../helpers';
import { ScenarioFormData, Step2DataForm, Step3DataForm } from '../types/form';
import { ScenarioRequestData } from '../types/scenarioRequest';

import { BeneficiariesDataMap } from './getBeneficiariesDataMap';

const getTriggers = (data: Step3DataForm): ApiTriggersDto | null => {
  let hasTrigger = false;
  const triggers: ApiTriggersDto = {};

  if (data.triggerType === ScenarioTriggerType.AfterDatetime && data.scheduledAt) {
    triggers.datetime = { scheduledAt: data.scheduledAt };
    hasTrigger = true;
  }

  if (data.triggerType === ScenarioTriggerType.AfterInactivity && data.afterDays) {
    triggers.inactivity = { afterDays: data.afterDays };
    hasTrigger = true;
  }

  if (!hasTrigger) {
    return null;
  }

  return triggers;
};

const getTransfers = ({
  step2,
  beneficiariesDataMap,
}: {
  step2: Step2DataForm;
  beneficiariesDataMap: BeneficiariesDataMap;
}): ApiTransferDto[] | null => {
  const isOneByOne = isOneByOneScenario(step2.transferType);
  const isAllAtOnce = isAllAtOnceScenario(step2.transferType);

  if (isOneByOne) {
    if (!step2.sequence1?.length || !step2.sequence2?.length || !step2.daysDelay) {
      return null;
    }

    const daysDelay = step2.daysDelay;

    const sequences = [step2.sequence1, step2.sequence2, step2.sequence3, step2.sequence4].filter(
      (el) => el !== undefined && el !== null,
    );

    const oneByOneSequences = sequences.reduce<ApiTransferDto[]>((acc, beneficiaryId, idx) => {
      const recipientProfileId = beneficiariesDataMap[beneficiaryId].centralDbId;
      const isLastSequence = idx === sequences.length - 1;

      acc.push({
        recipientProfileId,
        delayDays: daysDelay * idx,
        expirationDays: isLastSequence ? undefined : step2.daysDelay,
      });

      return acc;
    }, []);

    return oneByOneSequences;
  }

  if (isAllAtOnce) {
    if (!step2.beneficiaryIds?.length) {
      return null;
    }

    return step2.beneficiaryIds.map((beneficiaryId) => {
      const recipientProfileId = beneficiariesDataMap[beneficiaryId].centralDbId;
      return { recipientProfileId, delayDays: 0, expirationDays: undefined };
    });
  }

  return null;
};

type GetScenarioRequestDataReturnType =
  | {
      error: string;
      data?: ScenarioRequestData;
    }
  | {
      data: ScenarioRequestData;
      error?: string;
    };

export const getScenarioRequestData = ({
  scenario,
  beneficiariesDataMap,
  relatedDtoIdsMap,
}: {
  scenario: Required<ScenarioFormData>;
  beneficiariesDataMap: BeneficiariesDataMap;
  relatedDtoIdsMap: Map<CollectionName, string[]>;
}): GetScenarioRequestDataReturnType => {
  const triggers = getTriggers(scenario.step3);

  if (!triggers) {
    return { error: 'Missing triggers data' };
  }

  const transfers = getTransfers({ beneficiariesDataMap, step2: scenario.step2 });

  if (!transfers || !transfers.length) {
    return { error: 'Missing family members data' };
  }

  const relatedDtoIdsToAssetArray = Array.from(relatedDtoIdsMap.values());

  const notificationSettings =
    scenario.step4.daysBeforeTriggerNotification &&
    scenario.step4.daysBetweenNotifications &&
    scenario.step4.notificationTime
      ? {
          daysBeforeTrigger: scenario.step4.daysBeforeTriggerNotification,
          daysBetweenNotifications: scenario.step4.daysBetweenNotifications,
          notificationTime: scenario.step4.notificationTime,
        }
      : undefined;

  const scenarioData: ScenarioRequestData = {
    triggers,
    dtoIds: relatedDtoIdsToAssetArray.flat(),
    triggerCondition: ScenarioTriggerCondition.OneOf,
    transferCondition:
      scenario.step2.transferType === ScenarioTransferType.AllAtOnce
        ? ScenarioTransferCondition.AllOf
        : ScenarioTransferCondition.OneOf,
    transfers,
    notificationSettings,
  };

  return { data: scenarioData };
};
